import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeftIcon,
  TruckIcon,
  CalendarIcon,
  WrenchScrewdriverIcon,
  DocumentTextIcon,
  ChartBarIcon,
  ClockIcon,
  EyeIcon,
  PhotoIcon,
  CameraIcon,
  DocumentDuplicateIcon,
  PencilIcon,
  BookOpenIcon
} from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Tip } from '../components/ui/tip';
import { Input } from '../components/ui/input';
import { confirmDialog } from '../components/ConfirmDialog';
import QRCode from 'qrcode';
import InformeServicioDialog from '../components/informes/InformeServicioDialog';

interface Vehicle {
  id: string;
  make?: string;
  brand?: string;
  model: string;
  licensePlate: string;
  year: string;
  km?: string;
  mileage?: string;
  color?: string;
  engine?: string;
}

interface Service {
  id: string;
  serviceType: string;
  workDescription: string;
  totalCost: number;
  status: string;
  createdAt: string;
  mileageAtService?: number;
  photos?: string[];
  issuesFound?: any[];
  partsUsed?: any[];
  appointmentId?: string;
  appointmentCode?: string;
}

interface Appointment {
  id: string;
  appointmentCode?: string;
  serviceType: string;
  description?: string;
  scheduledDate: string;
  status: string;
  totalCost?: number;
  products?: any[];
}

interface Budget {
  id: string;
  budgetNumber?: string;
  description?: string;
  total: number;
  status: string;
  createdAt: string;
  validUntil?: string;
}

const VehicleHistory: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { vehicleId } = useParams<{ vehicleId: string }>();
  // Libreta de service digital (link publico permanente + QR)
  const [libretaAbierta, setLibretaAbierta] = useState(false);
  const [libretaUrl, setLibretaUrl] = useState('');
  const [libretaQr, setLibretaQr] = useState('');
  const abrirLibreta = async () => {
    try {
      const r = await axios.post(`/api/vehicle-book/${vehicleId}/link`);
      const url = r.data?.url;
      if (!url) throw new Error('sin url');
      setLibretaUrl(url);
      setLibretaQr(await QRCode.toDataURL(url, { width: 480, margin: 1 }));
      setLibretaAbierta(true);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'No se pudo generar el link de la libreta');
    }
  };
  const [searchParams] = useSearchParams();
  const clientName = searchParams.get('clientName') || '';
  const goBack = searchParams.get('from') || '/clients';

  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [services, setServices] = useState<Service[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [budgets, setBudgets] = useState<Budget[]>([]);
  // Diagnósticos del vehículo (checklist semáforo) + evolución entre visitas
  const [actas, setActas] = useState<any[]>([]);
  const [diagnostics, setDiagnostics] = useState<Array<{
    id: string; diagnosisNumber: string; status: string; mileage?: number | null; createdAt: string;
    performedByName?: string | null;
    counts: { ok: number; atencion: number; urgente: number };
    items: Array<{ pointKey: string | null; label: string; state: string | null }>;
  }>>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalServices: 0,
    totalAppointments: 0,
    totalBudgets: 0,
    lastServiceDate: null as string | null
  });

  useEffect(() => {
    if (vehicleId) {
      fetchData();
    }
  }, [vehicleId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [vehicleRes, servicesRes, appointmentsRes, budgetsRes, remitosRes, diagRes, actasRes] = await Promise.all([
        axios.get(`/api/vehicles/${vehicleId}`),
        axios.get(`/api/service-reports?vehicleId=${vehicleId}`),
        axios.get(`/api/appointments-enhanced?vehicleId=${vehicleId}`),
        axios.get(`/api/budgets?vehicleId=${vehicleId}`),
        axios.get(`/api/remitos?vehicleId=${vehicleId}`).catch(() => ({ data: { remitos: [] } })),
        axios.get(`/api/diagnostics/by-vehicle/${vehicleId}`).catch(() => ({ data: { diagnostics: [] } })),
        axios.get('/api/receptions', { params: { vehicleId } }).catch(() => ({ data: { actas: [] } }))
      ]);
      setDiagnostics(diagRes.data.diagnostics || []);
      setActas(actasRes.data.actas || []);

      const v = vehicleRes.data.vehicle || vehicleRes.data;
      setVehicle(v);

      const svcRaw = (servicesRes.data.reports || []).filter((s: Service) => s.status !== 'cancelled');
      const appts = (appointmentsRes.data.appointments || []).filter((a: Appointment) => a.status !== 'cancelled');

      // El estado real de la OT es el del turno: si el service-report está vinculado a un turno,
      // usar el estado del turno (el report a veces queda desincronizado, ej. in_progress tras completar).
      const apptStatusById = new Map<string, string>(appts.map((a: any) => [a.id, a.status]));
      const apptCodeById = new Map<string, string>(appts.map((a: any) => [a.id, a.appointmentCode]));
      const svcRawMapped = svcRaw.map((s: any) => ({
        ...s,
        status: (s.appointmentId && apptStatusById.get(s.appointmentId)) || s.status,
      }));
      const bdgts = (budgetsRes.data.budgets || []).filter((b: Budget) => b.status !== 'cancelled' && b.status !== 'rejected');

      // Todos los remitos del vehiculo (el remito es el registro completo de la OT: km, items, total, detalle)
      const remitosArr = (remitosRes.data.remitos || remitosRes.data || []).filter(
        (r: any) => r && r.vehicleId === vehicleId && r.status !== 'cancelled'
      );
      setRawRemitos(remitosArr);

      // Deduplicación: si el turno ya tiene un remito, ocultamos el service-report (evita filas dobles por la misma OT)
      const appointmentIdsConRemito = new Set(
        remitosArr.map((r: any) => r.appointmentId || r.relatedAppointmentId).filter(Boolean)
      );
      const svcFiltered = svcRawMapped.filter(
        (s: any) => !(s.appointmentId && appointmentIdsConRemito.has(s.appointmentId))
      );

      const remitoServices: Service[] = remitosArr.map((r: any) => {
        const itemsArr = Array.isArray(r.items) ? r.items : [];
        const serviceTypeStr = itemsArr.length > 0
          ? itemsArr.map((it: any) => it.name || it.description).filter(Boolean).join(', ')
          : (r.type || 'Remito');
        return {
          id: r.id,
          serviceType: serviceTypeStr,
          workDescription: r.observations || '',
          totalCost: Number(r.totalContado || r.estimatedValue || 0),
          status: r.status === 'issued' ? 'completed' : (r.status || 'pending'),
          createdAt: r.createdAt || r.issuedAt,
          mileageAtService: r.mileage != null && r.mileage !== '' ? Number(r.mileage) : undefined,
          photos: [],
          issuesFound: [],
          partsUsed: itemsArr,
          appointmentId: r.appointmentId || r.relatedAppointmentId || undefined,
          // Mostrar el codigo de OT del turno; si no hay, el numero de remito
          appointmentCode: apptCodeById.get(r.appointmentId || r.relatedAppointmentId) || r.remitNumber || ''
        } as Service;
      });

      const svc = [...svcFiltered, ...remitoServices].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      setServices(svc);
      setAppointments(appts);
      setBudgets(bdgts);

      const completed = svc.filter((s: Service) => s.status === 'completed');
      const lastService = completed.length > 0
        ? completed.sort((a: Service, b: Service) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]
        : null;

      setStats({
        totalServices: svc.length,
        totalAppointments: appts.length,
        totalBudgets: bdgts.length,
        lastServiceDate: lastService?.createdAt || null
      });
    } catch (error) {
      console.error('Error fetching vehicle history:', error);
      toast.error('Error al cargar el historial');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatCurrency = (amount: number) => {
    if (amount === null || amount === undefined || isNaN(amount)) return '-';
    return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    const config: Record<string, { label: string; variant: string }> = {
      completed: { label: 'Completado', variant: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/20' },
      in_progress: { label: 'En Progreso', variant: 'bg-blue-500/15 text-blue-500 border-blue-500/20' },
      pending: { label: 'Pendiente', variant: 'bg-yellow-500/15 text-yellow-500 border-yellow-500/20' },
      confirmed: { label: 'Confirmado', variant: 'bg-sky-500/15 text-sky-500 border-sky-500/20' },
      cancelled: { label: 'Cancelado', variant: 'bg-red-500/15 text-red-500 border-red-500/20' },
      approved: { label: 'Aprobado', variant: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/20' },
      rejected: { label: 'Rechazado', variant: 'bg-red-500/15 text-red-500 border-red-500/20' },
      draft: { label: 'Borrador', variant: 'bg-gray-500/15 text-gray-500 border-gray-500/20' },
      sent: { label: 'Enviado', variant: 'bg-blue-500/15 text-blue-500 border-blue-500/20' },
    };
    const c = config[status] || { label: status, variant: 'bg-muted text-muted-foreground' };
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${c.variant}`}>{c.label}</span>;
  };

  const tabs = [
    { id: 'overview', name: 'Resumen', icon: ChartBarIcon },
    { id: 'services', name: 'Servicios', icon: WrenchScrewdriverIcon },
    { id: 'appointments', name: 'Turnos', icon: CalendarIcon },
    { id: 'budgets', name: 'Presupuestos', icon: DocumentTextIcon }
  ];

  const handleOpenAppointment = (appt: Appointment) => {
    if (hasPermission('appointments')) {
      navigate(`/appointments-enhanced?q=${encodeURIComponent(appt.appointmentCode || appt.id)}`);
      return;
    }
    setInformeOtId(appt.id);
  };

  // Informe de servicio de la OT (reemplaza al "Documento de Servicio", v12.3.0)
  const [informeOtId, setInformeOtId] = useState<string | null>(null);
  const [viewRemito, setViewRemito] = useState<any | null>(null);
  const [rawRemitos, setRawRemitos] = useState<any[]>([]);
  const [kmInput, setKmInput] = useState('');
  const [savingKm, setSavingKm] = useState(false);
  const [editingKm, setEditingKm] = useState(false);
  const [showRemitoValues, setShowRemitoValues] = useState(false);

  const handleViewService = (service: Service) => {
    const remito = rawRemitos.find((r: any) => String(r.id) === String(service.id));
    if (remito) {
      const items = Array.isArray(remito.items)
        ? remito.items
        : (() => { try { return JSON.parse(remito.items || '[]'); } catch { return []; } })();
      setKmInput(remito.mileage != null && remito.mileage !== '' ? String(remito.mileage) : '');
      setEditingKm(false);
      setShowRemitoValues(false);
      setViewRemito({ ...remito, items });
      return;
    }
    // Sin remito: el informe de servicio de la OT
    if (service.appointmentId) setInformeOtId(service.appointmentId);
  };

  const handleSaveKm = async () => {
    if (!viewRemito) return;
    const km = parseInt(kmInput, 10);
    if (isNaN(km) || km < 0) {
      toast.error('Ingresá un km válido');
      return;
    }
    // Km menor al de la ficha: avisar (suele ser typo; la ficha no retrocede)
    const fichaKm = Number((vehicle as any)?.mileage || (vehicle as any)?.km) || 0;
    if (fichaKm > 0 && km < fichaKm) {
      const ok = await confirmDialog({
        title: 'Km menor al registrado',
        description: `La ficha del auto tiene ${fichaKm.toLocaleString('es-AR')} km y estás cargando ${km.toLocaleString('es-AR')}. Si es un error de tipeo, corregilo. La ficha del vehículo no retrocede.`,
        confirmText: 'Guardar igual'
      });
      if (!ok) return;
    }
    try {
      setSavingKm(true);
      await axios.put(`/api/remitos/${viewRemito.id}/mileage`, { mileage: km });
      setViewRemito((prev: any) => (prev ? { ...prev, mileage: km } : prev));
      setRawRemitos((prev: any[]) => prev.map((r) => (r.id === viewRemito.id ? { ...r, mileage: km } : r)));
      setServices((prev) => prev.map((s) => (s.id === viewRemito.id ? { ...s, mileageAtService: km } : s)));
      setEditingKm(false);
      toast.success('Km guardado');
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'No se pudo guardar el km');
    } finally {
      setSavingKm(false);
    }
  };

  const handleOpenBudget = (budget: Budget) => {
    if (hasPermission('budgets')) {
      navigate(`/budgets?q=${encodeURIComponent(budget.budgetNumber || budget.id)}`);
      return;
    }
    window.open(`/api/budgets/${budget.id}/pdf`, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="text-center py-12">
        <TruckIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">Vehículo no encontrado</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate(goBack)}>Volver</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => navigate(goBack)}>
            <ArrowLeftIcon className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              {vehicle.make || vehicle.brand} {vehicle.model}
            </h1>
            <p className="text-sm text-muted-foreground">
              Patente: {vehicle.licensePlate} &bull; Año: {vehicle.year}
              {vehicle.km || vehicle.mileage ? ` \u2022 ${Number(vehicle.km || vehicle.mileage).toLocaleString()} km` : ''}
              {clientName ? ` \u2022 ${clientName}` : ''}
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" className="gap-2 self-start sm:self-auto" onClick={abrirLibreta}>
          <BookOpenIcon className="h-4 w-4" />
          Libreta digital
        </Button>
      </div>

      {/* Libreta de service digital: link permanente + QR para el cliente */}
      <Dialog open={libretaAbierta} onOpenChange={setLibretaAbierta}>
        <DialogContent className="max-w-sm">
          <div className="space-y-3 text-center">
            <h3 className="text-lg font-semibold">Libreta de service digital</h3>
            <p className="text-sm text-muted-foreground">
              Link permanente de este veh\u00edculo: el cliente ve su historial y los pr\u00f3ximos services. Imprim\u00ed el QR para el parabrisas o mandale el link.
            </p>
            {libretaQr && <img src={libretaQr} alt="QR de la libreta" className="mx-auto h-52 w-52 rounded-lg border" />}
            {libretaUrl && (
              <p className="break-all rounded-md bg-muted px-2 py-1.5 text-xs">{libretaUrl}</p>
            )}
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => { navigator.clipboard.writeText(libretaUrl); toast.success('Link copiado'); }}
              >
                Copiar link
              </Button>
              <Button
                className="flex-1 bg-green-600 hover:bg-green-700"
                onClick={() => {
                  const texto = `Hola! Te comparto la libreta de service digital de tu ${vehicle.make || ''} ${vehicle.model || ''}: ${libretaUrl}`;
                  window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, '_blank');
                }}
              >
                WhatsApp
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Servicios', value: String(stats.totalServices), icon: WrenchScrewdriverIcon, color: 'text-blue-500 bg-blue-500/10' },
          { label: 'Turnos', value: String(stats.totalAppointments), icon: CalendarIcon, color: 'text-violet-500 bg-violet-500/10' },
          { label: 'Presupuestos', value: String(stats.totalBudgets), icon: DocumentTextIcon, color: 'text-emerald-500 bg-emerald-500/10' },
          { label: 'Último Servicio', value: stats.lastServiceDate ? formatDate(stats.lastServiceDate) : 'N/A', icon: ClockIcon, color: 'text-orange-500 bg-orange-500/10' },
        ].map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.label}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${stat.color}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground truncate">{stat.label}</p>
                    <p className="text-lg font-bold truncate">{stat.value}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Tabs */}
      <div className="border-b">
        <nav className="flex gap-6">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Vehicle info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Información del Vehículo</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Marca', value: vehicle.make || vehicle.brand || '-' },
                  { label: 'Modelo', value: vehicle.model || '-' },
                  { label: 'Patente', value: vehicle.licensePlate || '-' },
                  { label: 'Año', value: vehicle.year || '-' },
                  { label: 'Kilometraje', value: vehicle.km || vehicle.mileage ? `${Number(vehicle.km || vehicle.mileage).toLocaleString()} km` : '-' },
                  { label: 'Color', value: vehicle.color || '-' },
                  { label: 'Motor', value: vehicle.engine || '-' },
                  { label: 'Cliente', value: clientName || '-' },
                ].map((item) => (
                  <div key={item.label}>
                    <p className="text-xs text-muted-foreground">{item.label}</p>
                    <p className="text-sm font-medium">{item.value}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Recent services */}
          {services.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Servicios Recientes</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>OT</TableHead>
                      <TableHead>Fecha</TableHead>
                      <TableHead>KM</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="text-center">Visualizar</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {services.slice(0, 5).map((service) => (
                      <TableRow key={service.id}>
                        <TableCell className="font-medium text-primary">
                          {service.appointmentCode || '-'}
                        </TableCell>
                        <TableCell>{formatDate(service.createdAt)}</TableCell>
                        <TableCell>{service.mileageAtService ? `${service.mileageAtService.toLocaleString()} km` : '-'}</TableCell>
                        <TableCell>{getStatusBadge(service.status)}</TableCell>
                        <TableCell className="text-center">
                          <Tip label="Visualizar">
                            <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => handleViewService(service)}>
                              <EyeIcon className="h-4 w-4" />
                            </Button>
                          </Tip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {services.length > 5 && (
                  <div className="p-3 text-center border-t">
                    <Button variant="ghost" size="sm" onClick={() => setActiveTab('services')}>
                      Ver todos los servicios ({services.length})
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {activeTab === 'services' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Historial de Servicios ({services.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {services.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>OT</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead>KM</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Adjuntos</TableHead>
                    <TableHead className="text-center">Visualizar</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {services.map((service) => (
                    <TableRow key={service.id}>
                      <TableCell className="font-medium text-primary">
                        {service.appointmentCode || '-'}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">{formatDate(service.createdAt)}</TableCell>
                      <TableCell>{service.mileageAtService ? `${service.mileageAtService.toLocaleString()}` : '-'}</TableCell>
                      <TableCell>{getStatusBadge(service.status)}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          {service.photos && service.photos.length > 0 && (
                            <span className="flex items-center gap-1 text-xs text-muted-foreground">
                              <PhotoIcon className="h-3 w-3" />{service.photos.length}
                            </span>
                          )}
                          {service.partsUsed && service.partsUsed.length > 0 && (
                            <span className="flex items-center gap-1 text-xs text-muted-foreground">
                              <DocumentDuplicateIcon className="h-3 w-3" />{service.partsUsed.length}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Tip label="Visualizar">
                          <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => handleViewService(service)}>
                            <EyeIcon className="h-4 w-4" />
                          </Button>
                        </Tip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12">
                <WrenchScrewdriverIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No hay servicios registrados</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'appointments' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Historial de Turnos ({appointments.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {appointments.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>OT</TableHead>
                    <TableHead>Servicio</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-center">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {appointments.map((appt) => (
                    <TableRow key={appt.id}>
                      <TableCell className="font-medium text-primary">
                        {appt.appointmentCode || '-'}
                      </TableCell>
                      <TableCell className="font-medium max-w-[250px] truncate">{appt.serviceType}</TableCell>
                      <TableCell className="whitespace-nowrap">{formatDate(appt.scheduledDate)}</TableCell>
                      <TableCell>{getStatusBadge(appt.status)}</TableCell>
                      <TableCell className="text-center">
                        <Tip label="Abrir OT">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => handleOpenAppointment(appt)}
                          >
                            <EyeIcon className="h-4 w-4" />
                          </Button>
                        </Tip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12">
                <CalendarIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No hay turnos registrados</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'budgets' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Historial de Presupuestos ({budgets.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {budgets.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Código</TableHead>
                    <TableHead>Descripción</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Monto</TableHead>
                    <TableHead className="text-center">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {budgets.map((budget) => (
                    <TableRow key={budget.id}>
                      <TableCell className="whitespace-nowrap">{formatDate(budget.createdAt)}</TableCell>
                      <TableCell className="font-medium text-primary">{budget.budgetNumber || '-'}</TableCell>
                      <TableCell className="max-w-[250px] truncate">{budget.description || 'Presupuesto'}</TableCell>
                      <TableCell>{getStatusBadge(budget.status)}</TableCell>
                      <TableCell className="text-right font-medium whitespace-nowrap">
                        {formatCurrency(Number(budget.total))}
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 gap-1"
                          onClick={() => handleOpenBudget(budget)}
                        >
                          <EyeIcon className="h-3.5 w-3.5" />
                          Ver presupuesto
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12">
                <DocumentTextIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No hay presupuestos registrados</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Actas de recepción: cómo entró el auto en cada visita. Es lo que se
          consulta si el cliente reclama un daño que ya estaba. */}
      {actas.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Actas de recepción ({actas.length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {actas.map((a: any) => (
              <button
                key={a.id}
                type="button"
                onClick={() => navigate(`/actas/${a.id}`)}
                className="flex w-full items-center gap-3 rounded-lg border p-2.5 text-left transition-colors hover:bg-muted"
              >
                {a.photos?.[0]?.path
                  ? <img src={a.photos[0].path} alt="" className="h-12 w-16 shrink-0 rounded border object-cover" />
                  : <div className="flex h-12 w-16 shrink-0 items-center justify-center rounded border bg-muted"><CameraIcon className="h-5 w-5 text-muted-foreground" /></div>}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">
                    {new Date(a.createdAt).toLocaleDateString('es-AR')} · {a.receptionNumber}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {a.photos?.length || 0} foto(s){a.km != null ? ` · ${Number(a.km).toLocaleString('es-AR')} km` : ''}{a.appointmentCode ? ` · ${a.appointmentCode}` : ''}
                  </p>
                </div>
              </button>
            ))}
          </CardContent>
        </Card>
      )}

      {diagnostics.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Diagnósticos ({diagnostics.length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Número</TableHead>
                  <TableHead>Resultado</TableHead>
                  <TableHead className="cq-hide-narrow">Revisado por</TableHead>
                  <TableHead className="cq-hide-narrow">Km</TableHead>
                  <TableHead className="text-center">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {diagnostics.map(d => (
                  <TableRow key={d.id}>
                    <TableCell className="whitespace-nowrap">{formatDate(d.createdAt)}</TableCell>
                    <TableCell className="font-medium text-primary">{d.diagnosisNumber}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-xs font-semibold">
                        {d.counts.urgente > 0 && <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400"><span className="inline-block h-2 w-2 rounded-full bg-red-500" />{d.counts.urgente}</span>}
                        {d.counts.atencion > 0 && <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400"><span className="inline-block h-2 w-2 rounded-full bg-amber-500" />{d.counts.atencion}</span>}
                        {d.counts.ok > 0 && <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400"><span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />{d.counts.ok}</span>}
                        {d.counts.urgente + d.counts.atencion + d.counts.ok === 0 && <span className="text-muted-foreground font-normal">Sin puntos</span>}
                      </div>
                    </TableCell>
                    <TableCell className="cq-hide-narrow">{d.performedByName || '-'}</TableCell>
                    <TableCell className="cq-hide-narrow whitespace-nowrap">{d.mileage ? `${Number(d.mileage).toLocaleString('es-AR')} km` : '-'}</TableCell>
                    <TableCell className="text-center">
                      <Button variant="outline" size="sm" className="h-7 gap-1" onClick={() => navigate(`/diagnostics/${d.id}/checklist`)}>
                        <EyeIcon className="h-3.5 w-3.5" />
                        Ver checklist
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Evolución entre visitas: mismo punto a lo largo de los últimos diagnósticos */}
            {diagnostics.length >= 2 && (() => {
              const lastDiags = diagnostics.slice(0, 3); // más recientes primero
              const pointLabels = new Map<string, string>();
              lastDiags.forEach(d => d.items.forEach(it => {
                if (it.pointKey && it.state && it.state !== 'ok') pointLabels.set(it.pointKey, it.label);
              }));
              if (pointLabels.size === 0) return null;
              const dotFor = (d: typeof lastDiags[number], key: string) => {
                const it = d.items.find(x => x.pointKey === key);
                if (!it || !it.state) return <span className="text-muted-foreground">—</span>;
                const cls = it.state === 'ok' ? 'bg-emerald-500' : it.state === 'atencion' ? 'bg-amber-500' : 'bg-red-500';
                return <span className={`inline-block h-2.5 w-2.5 rounded-full ${cls}`} />;
              };
              return (
                <div className="px-4 pb-4">
                  <p className="mb-2 text-sm font-semibold">Evolución (puntos con atención/urgente)</p>
                  <div className="overflow-x-auto">
                    <table className="text-sm">
                      <thead>
                        <tr>
                          <th className="pr-4 text-left font-medium text-muted-foreground">Punto</th>
                          {lastDiags.map(d => (
                            <th key={d.id} className="px-3 text-center font-medium text-muted-foreground whitespace-nowrap">{formatDate(d.createdAt)}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {Array.from(pointLabels.entries()).map(([key, label]) => (
                          <tr key={key}>
                            <td className="pr-4 py-1">{label}</td>
                            {lastDiags.map(d => (
                              <td key={d.id} className="px-3 py-1 text-center">{dotFor(d, key)}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      <Dialog open={!!viewRemito} onOpenChange={(o) => { if (!o) setViewRemito(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {viewRemito && (
            <div className="space-y-6">
              <div className="flex flex-wrap items-center justify-between gap-2 pr-6">
                <h3 className="text-lg font-semibold text-foreground">Remito {viewRemito.remitNumber || ''}</h3>
                {(viewRemito.appointmentId || viewRemito.relatedAppointmentId) && (
                  <Button type="button" variant="outline" size="sm" onClick={() => setInformeOtId(viewRemito.appointmentId || viewRemito.relatedAppointmentId)}>
                    <DocumentTextIcon className="mr-1.5 h-4 w-4" />Informe de servicio
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <h4 className="font-medium text-foreground">Información General</h4>
                  <div>
                    <p className="text-xs text-muted-foreground">Cliente</p>
                    <p className="font-medium text-foreground">{clientName || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Vehículo</p>
                    <p className="font-medium text-foreground">
                      {(vehicle?.licensePlate ? `${vehicle.licensePlate} - ` : '')}{vehicle?.make || vehicle?.brand || ''} {vehicle?.model || ''}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Estado</p>
                    <div className="mt-1">{getStatusBadge(viewRemito.status)}</div>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-medium text-foreground">Detalles</h4>
                  <div>
                    <p className="text-xs text-muted-foreground">Km del vehículo (al servicio)</p>
                    {editingKm ? (
                      <div className="mt-1 flex items-center gap-2">
                        <Input
                          type="number"
                          min={0}
                          value={kmInput}
                          onChange={(e) => setKmInput(e.target.value)}
                          placeholder="Ej: 85000"
                          className="h-8 w-36"
                          autoFocus
                        />
                        <Button size="sm" className="h-8" onClick={handleSaveKm} disabled={savingKm}>
                          {savingKm ? 'Guardando...' : 'Guardar'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8"
                          onClick={() => {
                            setKmInput(viewRemito.mileage != null && viewRemito.mileage !== '' ? String(viewRemito.mileage) : '');
                            setEditingKm(false);
                          }}
                        >
                          Cancelar
                        </Button>
                      </div>
                    ) : (
                      <div className="mt-1 flex items-center gap-2">
                        <p className="font-medium text-foreground">
                          {viewRemito.mileage != null && viewRemito.mileage !== ''
                            ? `${Number(viewRemito.mileage).toLocaleString('es-AR')} km`
                            : 'Sin cargar'}
                        </p>
                        <Button size="sm" variant="outline" className="h-7 gap-1" onClick={() => setEditingKm(true)}>
                          <PencilIcon className="h-3.5 w-3.5" />
                          Editar
                        </Button>
                      </div>
                    )}
                  </div>
                  {viewRemito.relatedBudgetId && (
                    <div>
                      <p className="text-xs text-muted-foreground">Presupuesto asociado</p>
                      <p className="font-medium text-foreground">
                        {budgets.find((b: any) => b.id === viewRemito.relatedBudgetId)?.budgetNumber || viewRemito.relatedBudgetId}
                      </p>
                    </div>
                  )}
                  {showRemitoValues && viewRemito.estimatedValue != null && (
                    <div>
                      <p className="text-xs text-muted-foreground">Valor Estimado</p>
                      <p className="font-medium text-green-600">{formatCurrency(Number(viewRemito.estimatedValue || 0))}</p>
                    </div>
                  )}
                  {viewRemito.observations && (
                    <div>
                      <p className="text-xs text-muted-foreground">Observaciones</p>
                      <p className="text-sm text-foreground">{viewRemito.observations}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-muted-foreground">Emitido por</p>
                    <p className="font-medium text-foreground">{viewRemito.createdByName || viewRemito.userName || 'Sistema'}</p>
                    {viewRemito.createdAt && <p className="text-xs text-muted-foreground">{formatDate(viewRemito.createdAt)}</p>}
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t">
                <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                  <h4 className="font-medium text-foreground">
                    {showRemitoValues ? 'Remito (con valores)' : 'Productos y Servicios'}
                  </h4>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" className="h-8" onClick={() => setShowRemitoValues((v) => !v)}>
                      {showRemitoValues ? 'Ver sin valores' : 'Ver valores (remito)'}
                    </Button>
                    <Button
                      size="sm"
                      className="h-8 gap-1"
                      onClick={() => window.open(`/remito/preview-print/${viewRemito.id}`, '_blank', 'noopener,noreferrer')}
                    >
                      <DocumentTextIcon className="h-4 w-4" />
                      Imprimir PDF
                    </Button>
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cant.</TableHead>
                      <TableHead>Descripción</TableHead>
                      {showRemitoValues && <TableHead className="text-right">Precio Unit.</TableHead>}
                      {showRemitoValues && <TableHead className="text-right">Total</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(Array.isArray(viewRemito.items) ? viewRemito.items : []).map((it: any, i: number) => (
                      <TableRow key={i}>
                        <TableCell>{it.quantity || 1}</TableCell>
                        <TableCell className="font-medium">{it.name || it.description || '-'}</TableCell>
                        {showRemitoValues && <TableCell className="text-right">{formatCurrency(Number(it.unitPrice || 0))}</TableCell>}
                        {showRemitoValues && <TableCell className="text-right">{formatCurrency(Number(it.totalPrice || (Number(it.unitPrice || 0) * Number(it.quantity || 1))))}</TableCell>}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {showRemitoValues && (
                  <div className="flex justify-end mt-3 text-base font-semibold text-foreground">
                    Total:&nbsp;{formatCurrency(Number(viewRemito.totalContado || viewRemito.estimatedValue || 0))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <InformeServicioDialog
        appointmentId={informeOtId}
        open={!!informeOtId}
        onOpenChange={(o) => { if (!o) setInformeOtId(null); }}
      />
    </div>
  );
};

export default VehicleHistory;
